/**
 * AppRoutes.jsx — application route table.
 * Shaped by prompt 35; MKT-01, MKT-05, MKT-08, MKT-10.
 * Invariants: a role page never grants access. /for/patient redirects to
 * /caremap2 and is registered ahead of /:country. /specialty-reviewer/join
 * renders the same invite page as /specialty-kb/join. On the md3c host, `/`
 * is a branch of the clean root route (the clinicalhud.com pattern), chosen
 * before RootRedirect. That route stays ahead of /:country. On the md3c host,
 * a path that is not an entry path renders Md3cNotFoundPage before the
 * signed-out sign-in redirect, including when a user is signed in.
 * Related: fusionMsiRoleSplashRoutes.jsx, reviewerJoinPaths.js,
 *   features/md3c/marketing/pages/Md3cHomePage.jsx,
 *   features/md3c/marketing/pages/Md3cNotFoundPage.jsx,
 *   RequireLauncherApp.jsx (news, asi/templates, chartmind-plus, carestart-plus, fitness, email).
 */
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "config/stripe";
import { LocationProvider } from "context/Location";
import { isDevAuthEnvironment } from "config/devAuthCodes";
import { fusionMsiRoleSplashRouteElements } from "routes/fusionMsiRoleSplashRoutes";
import {
  SPECIALTY_KB_JOIN_PATH,
  SPECIALTY_REVIEWER_JOIN_PATH,
} from "features/specialtyKb/reviewerJoinPaths";
import { isDevEnvironment } from "utils/isDevEnvironment";
import { liveProductPathRedirects } from "config/productSubdomains";
import ProductHostEntry, { ProductSubdomainReplace } from "routes/ProductHostEntry";
import { isSignedOutEntryPath, signedOutSignInTarget } from "routes/signedOutEntry";
import { useAuth } from "hooks/useAuth";
import Loading from "components/Loading";
import FusionMsiWaitPreviewPage from "pages/dev/FusionMsiWaitPreviewPage";
import { PublicComputerIdleGuard } from "hooks/usePublicComputerIdleSignOut";
// Lazy: keeps the heavy Gemini Live (@google/genai) bundle out of the main chunk.
const VoxPont2Page = lazy(() => import("pages/voxpont2/VoxPont2Page"));
// VoxPont family — each is a fully self-contained live-translation fork.
const VoxPont3Page = lazy(() => import("pages/voxpont3/VoxPont3Page"));
const VoxPont4Page = lazy(() => import("pages/voxpont4/VoxPont4Page"));
const VoxPont12Page = lazy(() => import("pages/voxpont12/VoxPont12Page"));
const VoxPont12AudioLabPage = lazy(() => import("pages/voxpont12/VoxPont12AudioLabPage"));
// VoxPont5 — advanced Medical fork of VoxPont4 with the adaptive clarifying loop.
const VoxPont5Page = lazy(() => import("pages/voxpont5/VoxPont5Page"));
// VoxPont201 — voxpont5 parity on the new voxpontCore2 engine (v2 baseline).
const VoxPont201Page = lazy(() => import("pages/voxpont201/VoxPont201Page"));
const VoxPont301Page = lazy(() => import("pages/voxpont301/VoxPont301Page"));
const VoxPont501Page = lazy(() => import("pages/voxpont501/VoxPont501Page"));
const VoxPontOfflineLandingPage = lazy(() => import("pages/voxpontoffline/VoxPontOfflineLandingPage"));
const VoxPont302Page = lazy(() => import("pages/voxpont302/VoxPont302Page"));
// VoxPont204 — VoxPont204 + auto speaker detection by language ("Let The Man Speak").
const VoxPont204Page = lazy(() => import("pages/voxpont204/VoxPont204Page"));
// VoxPont205 — neutral start + auto speaker detection ("Flexible Speaker Order, $$$$").
const VoxPont205Page = lazy(() => import("pages/voxpont205/VoxPont205Page"));
const VoxPont700Page = lazy(() => import("pages/voxpont700/VoxPont700Page"));
// VoxPont202 — VoxPont201 + one-way auto-failover to cascade on sustained POOR link.
const VoxPont202Page = lazy(() => import("pages/voxpont202/VoxPont202Page"));
const VoxPont20Page = lazy(() => import("pages/voxpont20/VoxPont20Page"));
const VoxPont30Page = lazy(() => import("pages/voxpont30/VoxPont30Page"));
const VoxPont40Page = lazy(() => import("pages/voxpont40/VoxPont40Page"));
const VoxPont50Page = lazy(() => import("pages/voxpont50/VoxPont50Page"));
const VoxPont60Page = lazy(() => import("pages/voxpont60/VoxPont60Page"));
const VoxPont70Page = lazy(() => import("pages/voxpont70/VoxPont70Page"));
// VoxPont100 — "styled" variant: VoxPont4 flow re-skinned in the navy/gold marketing brand.
const VoxPont100Page = lazy(() => import("pages/voxpont100/VoxPont100Page"));
// Experimental VP110–VP430 auth catalog
const VoxPont110Page = lazy(() => import("pages/voxpont110/VoxPont110Page"));
const VoxPont120Page = lazy(() => import("pages/voxpont120/VoxPont120Page"));
const VoxPont130Page = lazy(() => import("pages/voxpont130/VoxPont130Page"));
const VoxPont140Page = lazy(() => import("pages/voxpont140/VoxPont140Page"));
const VoxPont150Page = lazy(() => import("pages/voxpont150/VoxPont150Page"));
const VoxPont160Page = lazy(() => import("pages/voxpont160/VoxPont160Page"));
const VoxPont170Page = lazy(() => import("pages/voxpont170/VoxPont170Page"));
const VoxPont180Page = lazy(() => import("pages/voxpont180/VoxPont180Page"));
const VoxPont190Page = lazy(() => import("pages/voxpont190/VoxPont190Page"));
const VoxPont200Page = lazy(() => import("pages/voxpont200/VoxPont200Page"));
const VoxPont210Page = lazy(() => import("pages/voxpont210/VoxPont210Page"));
const VoxPont220Page = lazy(() => import("pages/voxpont220/VoxPont220Page"));
const VoxPont230Page = lazy(() => import("pages/voxpont230/VoxPont230Page"));
const VoxPont240Page = lazy(() => import("pages/voxpont240/VoxPont240Page"));
const VoxPont250Page = lazy(() => import("pages/voxpont250/VoxPont250Page"));
const VoxPont260Page = lazy(() => import("pages/voxpont260/VoxPont260Page"));
const VoxPont270Page = lazy(() => import("pages/voxpont270/VoxPont270Page"));
const VoxPont280Page = lazy(() => import("pages/voxpont280/VoxPont280Page"));
const VoxPont290Page = lazy(() => import("pages/voxpont290/VoxPont290Page"));
const VoxPont300Page = lazy(() => import("pages/voxpont300/VoxPont300Page"));
const VoxPont310Page = lazy(() => import("pages/voxpont310/VoxPont310Page"));
const VoxPont320Page = lazy(() => import("pages/voxpont320/VoxPont320Page"));
const VoxPont330Page = lazy(() => import("pages/voxpont330/VoxPont330Page"));
const VoxPont340Page = lazy(() => import("pages/voxpont340/VoxPont340Page"));
const VoxPont350Page = lazy(() => import("pages/voxpont350/VoxPont350Page"));
const VoxPont360Page = lazy(() => import("pages/voxpont360/VoxPont360Page"));
const VoxPont370Page = lazy(() => import("pages/voxpont370/VoxPont370Page"));
const VoxPont380Page = lazy(() => import("pages/voxpont380/VoxPont380Page"));
const VoxPont390Page = lazy(() => import("pages/voxpont390/VoxPont390Page"));
const VoxPont400Page = lazy(() => import("pages/voxpont400/VoxPont400Page"));
const VoxPont410Page = lazy(() => import("pages/voxpont410/VoxPont410Page"));
const VoxPont420Page = lazy(() => import("pages/voxpont420/VoxPont420Page"));
const VoxPont430Page = lazy(() => import("pages/voxpont430/VoxPont430Page"));
// CareCompris — clinical AI translation; /carevoice is backed by CareCompris2.
const CareComprisPage = lazy(() => import("pages/carecompris/CareComprisPage"));
const CareCompris2Page = lazy(() => import("pages/carecompris2/CareCompris2Page"));
// Lazy: CareStart3 pulls in the symptom-checker / CareMap / triage subsystem.
const CareStart3Page = lazy(() => import("pages/pages/scmap4/CareStart3Page"));
// CareStart Plus — modular SecureLink TCIs (registration/triage/HPI/discharge).
// Independent of classic CareStart DDx (`/carestart3`). Not an empty shell.
const CareStartPlusPage = lazy(() => import("pages/carestartPlus/CareStartPlusPage"));
const CareStartPlusRegistrationPage = lazy(() =>
  import("pages/carestartPlus/CareStartPlusRegistrationPage"),
);
const CareStartPlusTriagePage = lazy(() =>
  import("pages/carestartPlus/CareStartPlusTriagePage"),
);
const CareStartPlusHpiPage = lazy(() =>
  import("pages/carestartPlus/CareStartPlusHpiPage"),
);
const CareStartPlusDischargePage = lazy(() =>
  import("pages/carestartPlus/CareStartPlusDischargePage"),
);
const VisitStartPage = lazy(() => import("pages/visitStart/VisitStartPage"));
const VisitStartAdminGovernancePage = lazy(() =>
  import("pages/visitStart/VisitStartAdminGovernancePage"),
);
const ChartMindPlusPage = lazy(() => import("pages/chartmindPlus/ChartMindPlusPage"));
const ChartMindPlusDifferentialPage = lazy(() =>
  import("pages/chartmindPlus/ChartMindPlusDifferentialPage"),
);
// Public CareStart (English default) + facility variants — static routes before :variantSlug.
const Scmap4Page = lazy(() => import("pages/pages/scmap4/Scmap4Page"));
const Scmap4HelpPage = lazy(() => import("pages/pages/scmap4/Scmap4HelpPage"));
const CareStartPublicSlugPage = lazy(() =>
  import("pages/pages/scmap4/CareStartPublicSlugPage")
);
// CareVerify — standalone Tier 2 booking (visit-type → facility → slot/window).
const VisitTypeSelfDesignationPage = lazy(() =>
  import("features/careverify/pages/VisitTypeSelfDesignationPage")
);
const CareVerifyBookingPage = lazy(() =>
  import("features/careverify/pages/CareVerifyBookingPage")
);
// Lazy: CareMap-1 standalone facility map (Leaflet + geofirestore) — same <Map>
// component CareStart's embedded CareMap-2 uses, over the shared `locations` data.
const MapPage = lazy(() => import("pages/map/MapPage"));
const CareMap2Page = lazy(() => import("pages/map/CareMap2Page"));
const HealthContoursPage = lazy(() =>
  import("pages/healthcontours/HealthContoursPage"),
);
const ClinicalHudMarketingSplashPage = lazy(() =>
  import(
    "features/clinicalHud/marketing/pages/ClinicalHudMarketingSplashPage"
  ),
);
const Md3cHomePage = lazy(() =>
  import("features/md3c/marketing/pages/Md3cHomePage"),
);
const Md3cCriticalChallengesPage = lazy(() =>
  import("features/md3c/marketing/pages/Md3cCriticalChallengesPage"),
);
const Md3cAboutPage = lazy(() =>
  import("features/md3c/marketing/pages/Md3cAboutPage"),
);
const Md3cNotFoundPage = lazy(() =>
  import("features/md3c/marketing/pages/Md3cNotFoundPage"),
);
const Md3cBlogIndexPage = lazy(() =>
  import("features/md3c/marketing/pages/Md3cBlogIndexPage"),
);
const Md3cEssayPage = lazy(() =>
  import("features/md3c/marketing/pages/Md3cEssayPage"),
);
const ClinicalHudMarketingHubPage = lazy(() =>
  import(
    "features/clinicalHud/marketing/pages/ClinicalHudMarketingHubPage"
  ),
);
const ClinicalHudMarketingHowItWorksPage = lazy(() =>
  import(
    "features/clinicalHud/marketing/pages/ClinicalHudMarketingHowItWorksPage"
  ),
);
const ClinicalHudMarketingDdxVsPearlsPage = lazy(() =>
  import(
    "features/clinicalHud/marketing/pages/ClinicalHudMarketingDdxVsPearlsPage"
  ),
);
const ClinicalHudMarketingRequestAccessPage = lazy(() =>
  import(
    "features/clinicalHud/marketing/pages/ClinicalHudMarketingRequestAccessPage"
  ),
);
const HealthContoursSignupPage = lazy(() =>
  import("pages/healthcontours/HealthContoursSignupPage"),
);
const HealthContoursAdvertisePage = lazy(() =>
  import("pages/healthcontours/HealthContoursAdvertisePage"),
);
const HealthContoursAudiencePage = lazy(() =>
  import("pages/healthcontours/HealthContoursAudiencePage"),
);
const CareMapRolePreviewPage =
  process.env.NODE_ENV !== "production"
    ? lazy(() => import("pages/dev/CareMapRolePreview"))
    : null;
const EhrDischargeLandingPage = lazy(() =>
  import("pages/discharge/EhrDischargeLandingPage"),
);
const AiListeningSignPage = lazy(() =>
  import("pages/print/AiListeningSignPage"),
);
const NewsPage = lazy(() => import("features/news/pages/NewsPage"));
const FitnessCalendarPage = lazy(() => import("features/fitness/pages/FitnessCalendarPage"));
const GarminLinkPage = lazy(() => import("features/fitness/pages/GarminLinkPage"));
const ShareFitnessAccessPage = lazy(() => import("features/fitness/pages/ShareFitnessAccessPage"));
const ClinicianFitnessViewPage = lazy(() =>
  import("features/fitness/pages/ClinicianFitnessViewPage"),
);
const EmailInboxPage = lazy(() => import("features/email/pages/EmailInboxPage"));
const EmailThreadPage = lazy(() => import("features/email/pages/EmailThreadPage"));
const EmailComposePage = lazy(() => import("features/email/pages/EmailComposePage"));
// Applied Superintelligence — trainer / manager dashboards + workflow canvas.
const AsiTrainerDashboard = lazy(() => import("features/asi/pages/TrainerDashboard"));
const AsiInteractionManagerDashboard = lazy(() =>
  import("features/asi/pages/InteractionManagerDashboard"),
);
const AsiRoleAssignmentPage = lazy(() =>
  import("features/asi/pages/RoleAssignmentPage"),
);
const AsiGlobalManagerPage = lazy(() =>
  import("features/asi/pages/GlobalManagerPage"),
);
const AsiWorkflowCanvasPage = lazy(() => import("features/asi/pages/WorkflowCanvasPage"));
const AsiTemplateLibraryPage = lazy(() => import("features/asi/pages/TemplateLibraryPage"));
const AsiOccurrenceRunPage = lazy(() => import("features/asi/pages/OccurrenceRunPage"));
const AsiMarketingSplashPage = lazy(() =>
  import("features/asi/pages/AsiMarketingSplashPage"),
);
const AsiMarketingHubPage = lazy(() =>
  import("features/asi/pages/AsiMarketingHubPage"),
);
const AsiMarketingMethodPage = lazy(() =>
  import("features/asi/pages/AsiMarketingMethodPage"),
);
const AsiMarketingLearnersPage = lazy(() =>
  import("features/asi/pages/AsiMarketingLearnersPage"),
);
const AsiMarketingFacultyPage = lazy(() =>
  import("features/asi/pages/AsiMarketingFacultyPage"),
);
const AsiMarketingAppsPage = lazy(() =>
  import("features/asi/pages/AsiMarketingAppsPage"),
);
const AsiMarketingContactPage = lazy(() =>
  import("features/asi/pages/AsiMarketingContactPage"),
);
const SpecialtyKbEditorsDashboardPage = lazy(() =>
  import("features/specialtyKb/pages/EditorsDashboardPage"),
);
const SpecialtyKbReviewerInviteJoinPage = lazy(() =>
  import("features/specialtyKb/pages/ReviewerInviteJoinPage"),
);
const SpecialtyKbDifferentialDiagnosisPage = lazy(() =>
  import("features/specialtyKb/pages/DifferentialDiagnosisPage"),
);
const ClinicalHudPage = lazy(() =>
  import("features/clinicalHud/app/pages/ClinicalHudPage"),
);
const ClinicalHud2ListPage = lazy(() =>
  import("features/clinicalHud2/pages/ClinicalHud2ListPage"),
);
const ClinicalHud2WorkspacePage = lazy(() =>
  import("features/clinicalHud2/pages/ClinicalHud2WorkspacePage"),
);
const ChartMind7ListPage = lazy(() =>
  import("features/chartMind7/pages/ChartMind7ListPage"),
);
const ChartMind7WorkspacePage = lazy(() =>
  import("features/chartMind7/pages/ChartMind7WorkspacePage"),
);
const ClinicalHud3TeachingListPage = lazy(() =>
  import("features/clinicalHud3/pages/ClinicalHud3TeachingListPage"),
);
const ClinicalHud3LiveListPage = lazy(() =>
  import("features/clinicalHud3/pages/ClinicalHud3LiveListPage"),
);
const ClinicalHud3AdminCasesPage = lazy(() =>
  import("features/clinicalHud3/pages/ClinicalHud3AdminCasesPage"),
);
const ClinicalHud3AdminUsagePage = lazy(() =>
  import("features/clinicalHud3/pages/ClinicalHud3AdminUsagePage"),
);
const Msi3FacultyCasesPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3FacultyCasesPage"),
);
const Msi3AdminTagsPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3AdminTagsPage"),
);
const Msi3AdminAccessPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3AdminAccessPage"),
);
const Msi3FacultyJoinPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3FacultyJoinPage"),
);
const Msi3OrgSeatsPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3OrgSeatsPage"),
);
const Msi3OrgGlobalCasesPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3OrgGlobalCasesPage"),
);
const Msi3LearnerProgressPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3LearnerProgressPage"),
);
const Msi3OrgProgressPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3OrgProgressPage"),
);
const Msi3MarketingPitchPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3MarketingPages").then((m) => ({
    default: m.Msi3MarketingPitchPage,
  })),
);
const Msi3MarketingPricingPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3MarketingPages").then((m) => ({
    default: m.Msi3MarketingPricingPage,
  })),
);
const Msi3MarketingFacultyPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3MarketingPages").then((m) => ({
    default: m.Msi3MarketingFacultyPage,
  })),
);
const Msi3MarketingFaqPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3MarketingPages").then((m) => ({
    default: m.Msi3MarketingFaqPage,
  })),
);
const Msi3CheckoutSuccessPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3MarketingPages").then((m) => ({
    default: m.Msi3CheckoutSuccessPage,
  })),
);
const Msi3CheckoutCancelPage = lazy(() =>
  import("features/msi3Teaching/pages/Msi3MarketingPages").then((m) => ({
    default: m.Msi3CheckoutCancelPage,
  })),
);
const ClinicalHud3WorkspacePage = lazy(() =>
  import("features/clinicalHud3/pages/ClinicalHud3WorkspacePage"),
);
const ClinicalHud4TeachingListPage = lazy(() =>
  import("features/clinicalHud4/pages/ClinicalHud4TeachingListPage"),
);
const ClinicalHud5TeachingListPage = lazy(() =>
  import("features/clinicalHud5/pages/ClinicalHud5TeachingListPage"),
);
const AsiTeachingLauncherPage = lazy(() =>
  import("features/msi3Teaching/pages/AsiTeachingLauncherPage"),
);
const FactPatternListPage = lazy(() =>
  import("features/msi3Teaching/pages/AsiTeachingAppRoutes").then((m) => ({
    default: m.FactPatternListPage,
  })),
);
const FactPatternSessionPage = lazy(() =>
  import("features/msi3Teaching/pages/AsiTeachingAppRoutes").then((m) => ({
    default: m.FactPatternSessionPage,
  })),
);
const FactPatternFacultyPage = lazy(() =>
  import("features/msi3Teaching/pages/AsiTeachingAppRoutes").then((m) => ({
    default: m.FactPatternFacultyPage,
  })),
);
const RadioCallListPage = lazy(() =>
  import("features/msi3Teaching/pages/AsiTeachingAppRoutes").then((m) => ({
    default: m.RadioCallListPage,
  })),
);
const RadioCallSessionPage = lazy(() =>
  import("features/msi3Teaching/pages/AsiTeachingAppRoutes").then((m) => ({
    default: m.RadioCallSessionPage,
  })),
);
const RadioCallFacultyPage = lazy(() =>
  import("features/msi3Teaching/pages/AsiTeachingAppRoutes").then((m) => ({
    default: m.RadioCallFacultyPage,
  })),
);
const ChartMindIRosterPage = lazy(() =>
  import("features/chartMindI/pages/ChartMindIRosterPage"),
);
const ChartMindILaunchPage = lazy(() =>
  import("features/chartMindI/pages/ChartMindILaunchPage"),
);
const ChartMindIWorkspacePage = lazy(() =>
  import("features/chartMindI/pages/ChartMindIWorkspacePage"),
);
const ChartMindINotePage = lazy(() =>
  import("features/chartMindI/pages/ChartMindINotePage"),
);
const ChartMindI2RosterPage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2RosterPage"),
);
const ChartMindI2LaunchPage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2LaunchPage"),
);
const ChartMindI2WorkspacePage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2WorkspacePage"),
);
const ChartMindI2NotePage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2NotePage"),
);
const ChartMindI2TeamsPage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2TeamsPage"),
);
const ChartMindI2TeamCensusPage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2TeamCensusPage"),
);
const ChartMindI2TeamSettingsPage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2TeamSettingsPage"),
);
const ChartMindI2JoinTeamPage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2JoinTeamPage"),
);
const ChartMindI2MyTodosPage = lazy(() =>
  import("features/chartMindI2/pages/ChartMindI2MyTodosPage"),
);
// Public VoxPont marketing hub + domain variant pages (bespoke brand landing).
const VoxPontMarketingPage = lazy(() =>
  import("pages/voxpontmarketing/VoxPontMarketingPage")
);
// BYOI ("Bring Your Own Institution") acquisition-channel landing page.
const VoxPontByoiPage = lazy(() =>
  import("pages/voxpontmarketing/VoxPontByoiPage")
);
// Downloadable VoxPont brand-assets board.
const VoxPontBrandPage = lazy(() =>
  import("pages/voxpontmarketing/VoxPontBrandPage")
);
// Canonical VoxPont marketing-materials reference (renders the source markdown).
const VoxPontMarketingReferencePage = lazy(() =>
  import("pages/voxpontMarketingReference/VoxPontMarketingReferencePage")
);
// DecisionPoint / FusionMSI — Medical SuperIntelligence™ + Continuity of Care (/dp namespace).
const FusionMsiMarketingRouter = lazy(() =>
  import("features/decisionPoint/pages/FusionMsiMarketingRouter")
);
const FusionMsiMarketingHubPage = lazy(() =>
  import("features/decisionPoint/pages/FusionMsiMarketingHubPage")
);
const RoleHubGate = lazy(() =>
  import("features/decisionPoint/pages/RoleHubGate")
);
const FusionMsiMarketingSubPage = lazy(() =>
  import("features/decisionPoint/pages/FusionMsiMarketingSubPage")
);
const FusionMsiAudiencePage = lazy(() =>
  import("features/decisionPoint/pages/FusionMsiAudiencePage")
);
const FusionMsiAboutPage = lazy(() =>
  import("features/decisionPoint/pages/FusionMsiAboutPage")
);
const FusionMsiBusinessModelPage = lazy(() =>
  import("features/decisionPoint/pages/FusionMsiBusinessModelPage")
);
const CareStartSponsorPage = lazy(() =>
  import("features/decisionPoint/pages/CareStartSponsorPage")
);
const FusionMsiGetOnTheMapPage = lazy(() =>
  import("features/decisionPoint/pages/FusionMsiGetOnTheMapPage")
);
const HistoricalPrecedentPage = lazy(() =>
  import("features/decisionPoint/pages/HistoricalPrecedentPage")
);
const Cities1Page = lazy(() => import("pages/cities1/Cities1Page"));
const CountryMarketingPage = lazy(() =>
  import("features/decisionPoint/pages/CountryMarketingPage")
);
const CityMarketingPage = lazy(() =>
  import("features/decisionPoint/pages/CityMarketingPage")
);
const PlatformRouter = lazy(() => import("features/decisionPoint/pages/PlatformRouter"));
const DpAuthPage = lazy(() => import("features/decisionPoint/pages/DpAuthPage"));
const DpJoinPage = lazy(() => import("features/decisionPoint/pages/DpJoinPage"));
const DpDisplayPage = lazy(() => import("features/decisionPoint/pages/DpDisplayPage"));
const DpAdminHomePage = lazy(() =>
  import("features/decisionPoint/pages/DpAdminHomePage")
);
const DpRecordEditorPage = lazy(() =>
  import("features/decisionPoint/admin/DpRecordEditorPage")
);
const DpReviewerAdminPage = lazy(() =>
  import("features/decisionPoint/pages/DpReviewerAdminPage")
);
const DpReviewPage = lazy(() => import("features/decisionPoint/pages/DpReviewPage"));
const DpCoveragePage = lazy(() => import("features/decisionPoint/pages/DpCoveragePage"));
const DpAuditPage = lazy(() => import("features/decisionPoint/pages/DpAuditPage"));
const DpImportPage = lazy(() => import("features/decisionPoint/pages/DpImportPage"));
const DpContentSourcesPage = lazy(() =>
  import("features/decisionPoint/pages/DpContentSourcesPage")
);
const DpLibraryPage = lazy(() => import("features/decisionPoint/pages/DpLibraryPage"));
const DpAccessRosterPage = lazy(() =>
  import("features/decisionPoint/pages/DpAccessRosterPage")
);
import DpLayout from "features/decisionPoint/components/DpLayout";
import DpRouteGate from "features/decisionPoint/components/DpRouteGate";
import RequireDpGlobalAdmin from "features/decisionPoint/components/RequireDpGlobalAdmin";
import {
  isVoxPontMarketingHost,
  isVoxPontAppDomain,
  MARKETING_SLUGS,
  marketingHubPath,
  marketingVariantPath,
} from "pages/voxpontmarketing/voxpontMarketingRouting";
import {
  isFusionMsiHost,
  MARKETING_SLUGS as FUSION_MSI_MARKETING_SLUGS,
  PLATFORM_SLUGS as FUSION_MSI_PLATFORM_SLUGS,
  AUDIENCE_SLUGS as FUSION_MSI_AUDIENCE_SLUGS,
  marketingPagePath as fusionMsiMarketingPagePath,
  recordEditorPath as fusionMsiRecordEditorPath,
  geoCityPath,
  geoCountryPath,
} from "features/decisionPoint/marketing/fusionMsiMarketingRouting";
import { getGeoCity, getGeoCountry } from "features/decisionPoint/marketing/geoMarketingData";
import { isHealthContoursHost } from "pages/healthcontours/healthContoursHost";
import { HEALTHCONTOURS_AUDIENCE_SLUGS } from "pages/healthcontours/healthContoursAudienceData";
import {
  isClinicalHudMarketingHost,
  MARKETING_SLUGS as CLINICALHUD_MARKETING_SLUGS,
  OVERVIEW_SLUG as CLINICALHUD_OVERVIEW_SLUG,
} from "features/clinicalHud/marketing/clinicalHudMarketingRouting";
import { isMd3cMarketingHost } from "features/md3c/marketing/md3cMarketingRouting";
import {
  isMsi3Host,
} from "features/msi3Teaching/msi3HostRouting";
import {
  isAsiMarketingHost,
  MARKETING_SLUGS as ASI_MARKETING_SLUGS,
  OVERVIEW_SLUG as ASI_OVERVIEW_SLUG,
} from "features/asi/marketing/asiMarketingRouting";
import RequireClinicalHudAccess from "features/clinicalHud/app/components/RequireClinicalHudAccess";
import RequireClinicalHud3Teaching from "features/clinicalHud3/components/RequireClinicalHud3Teaching";
import RequireMsi3Learner from "features/msi3Teaching/components/RequireMsi3Learner";
import RequireMsi3Faculty from "features/msi3Teaching/components/RequireMsi3Faculty";
import RequireMsi3OrgProgress from "features/msi3Teaching/components/RequireMsi3OrgProgress";
import RequireClinicalHud3LiveAccess from "features/clinicalHud3/components/RequireClinicalHud3LiveAccess";
import { ClinicalHud3SettingsProvider } from "features/clinicalHud3/clinicalHud3Settings";
import {
  CLINICAL_HUD3_LIVE,
  CLINICAL_HUD3_TEACHING,
} from "features/clinicalHud3/clinicalHud3WorkspaceConfig";
import { CLINICAL_HUD4_TEACHING } from "features/clinicalHud4/clinicalHud4WorkspaceConfig";
import { CLINICAL_HUD5_TEACHING } from "features/clinicalHud5/clinicalHud5WorkspaceConfig";
import RequireChartMindIAccess from "features/chartMindI/components/RequireChartMindIAccess";
import RequireChartMindI2Access from "features/chartMindI2/components/RequireChartMindI2Access";
import RequireLauncherApp from "components/appLauncher/RequireLauncherApp";

// Auth pages
import AuthPage from "pages/auth/AuthPage";
// VoxPont branded auth — statically imported (matches AuthPage), rendered at
// /voxpont-auth everywhere and at /auth on VoxPont app domains.
import VoxPontAuthPage from "pages/voxpontAuth/VoxPontAuthPage";
// VoxPont product picker / post-auth landing — statically imported (on the
// post-auth path, redirects to the user's saved product when one exists).
import VoxPontOnboardingPage from "pages/voxpontOnboarding/VoxPontOnboardingPage";
// VoxPont branded legal pages (Terms of Use / Privacy Policy) — bespoke navy/gold
// surface, linked from the VoxPont auth notice. Statically imported (matches the
// other VoxPont auth-adjacent pages).
import VoxPontLegalPage from "pages/voxpontLegal/VoxPontLegalPage";
// VoxPont Beta invite-claim landing — statically imported (matches VoxPontAuthPage).
import VoxPontBetaJoinPage from "pages/voxpontBetaJoin/VoxPontBetaJoinPage";
import VoxPontBetaRouteGate from "features/voxpontBeta/components/VoxPontBetaRouteGate";
// VoxPont Beta admin dashboard — gated to the admin custom claim below.
import AdminVoxPontBetaPage from "pages/adminVoxPontBeta/AdminVoxPontBetaPage";
// Public institutional BAA request form (no auth) + admin-gated BAA dashboard.
import VoxPontBaaRequestPage from "pages/voxpontBaaRequest/VoxPontBaaRequestPage";
// Public DocuSign return landing after the embedded-signing ceremony.
import VoxPontBaaSignedPage from "pages/voxpontBaaRequest/VoxPontBaaSignedPage";
import AdminBAAPage from "pages/adminBaa/AdminBAAPage";
import ResetPasswordPage from "pages/auth/ResetPasswordPage";
import WPAuthHandler from "pages/auth/WPAuthHandler";

// Dashboard pages
import DashboardPage from "pages/dashboard/DashboardPage";
import KijabeDashboard from "pages/dashboard/KijabeDashboard";

// Chat pages
import KijabeChatPage from "pages/KijabeChatPage";
import ChatPageWrapper from "pages/ChatPageWrapper";
import AskMeAnythingPage from "pages/AskMeAnythingPage";

// Virtual Queue pages
import PatientRegistration from "components/vaccine/queue/PatientRegistration";

// Legal pages
import PrivacyPolicy from "components/auth/documents/PrivacyPolicy";
import TermsOfUse from "components/auth/documents/TermsOfUse";
import Baa from "components/auth/documents/baa";
import CareVerifyTermsDraftPage from "pages/legal/CareVerifyTermsDraftPage";

// Account pages
import Verify from "components/dashboard/Verify";
import Onboarding from "components/auth/Onboarding";

import JoinPage from "pages/invitation/JoinPage";
import CheckInPage from "pages/checkin/CheckInPage";
import ShareLandingPage from "pages/share/ShareLandingPage";
const TciSessionConsentMintPage = lazy(() =>
  import("pages/share/TciSessionConsentMintPage"),
);
import ChampionVideoRolloutPage from "pages/rollout/ChampionVideoRolloutPage";

const RootRedirect = () => {
  const { user, userLoading, userData } = useAuth();
  if (userLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  // VoxPont users have no HealthDesk role — route them through the VoxPont
  // picker (which forwards to their saved product) instead of the dashboard.
  if (userData?.product === "voxpont") {
    return <Navigate to="/voxpont-start" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

/** App-host clean paths like /kenya → /dp/kenya when geo data exists. */
const GeoMarketingRootRedirect = () => {
  const { country, city } = useParams();
  if (city && getGeoCity(country, city)) {
    return <Navigate to={geoCityPath(country, city)} replace />;
  }
  if (getGeoCountry(country)) {
    return <Navigate to={geoCountryPath(country)} replace />;
  }
  return <RootRedirect />;
};

const UnmatchedRouteFallback = () => {
  const { pathname } = useLocation();
  if (isMd3cMarketingHost()) {
    return (
      <Suspense fallback={<Loading page />}>
        <Md3cNotFoundPage />
      </Suspense>
    );
  }
  if (isFusionMsiHost()) {
    return <Navigate to="/" replace />;
  }
  if (isMsi3Host()) {
    return <Navigate to="/" replace />;
  }
  if (isClinicalHudMarketingHost()) {
    return <Navigate to="/" replace />;
  }
  if (isAsiMarketingHost()) {
    return <Navigate to="/" replace />;
  }
  if (isVoxPontMarketingHost()) {
    return <Navigate to="/" replace />;
  }
  const parts = pathname.replace(/^\/+/, "").split("/").filter(Boolean);
  if (parts.length >= 1 && parts.length <= 2 && getGeoCountry(parts[0])) {
    const target =
      parts.length === 2 && getGeoCity(parts[0], parts[1])
        ? geoCityPath(parts[0], parts[1])
        : geoCountryPath(parts[0]);
    return <Navigate to={target} replace />;
  }
  return <RootRedirect />;
};

// Gate a route to platform super-admins (admin custom claim). Mirrors the
// isGlobalAdmin source used across the dashboard.
const RequireGlobalAdmin = ({ children }) => {
  const { isGlobalAdmin, userLoading } = useAuth();
  if (userLoading) return <Loading page />;
  return isGlobalAdmin ? children : <Navigate to="/" replace />;
};

const RequireSpecialtyKbEditor = ({ children }) => {
  const { isGlobalAdmin, tokenClaims, userLoading } = useAuth();
  if (userLoading) return <Loading page />;
  const isReviewer = tokenClaims?.specialtyKbReviewer === true;
  return isGlobalAdmin || isReviewer ? children : <Navigate to="/" replace />;
};

// MSI3 seat assignment — global admin or a school org-admin. UX guard only;
// assignMsi3OrgSeat re-checks org-admin membership on the server.
const RequireMsi3SeatManager = ({ children }) => {
  const { user, userData, isGlobalAdmin, userLoading } = useAuth();
  const location = useLocation();
  if (userLoading) return <Loading page />;
  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?next=${encodeURIComponent(next)}`} replace />;
  }
  const isOrgAdmin =
    userData?.role === "admin" || userData?.activeOrgRole?.role === "admin";
  return isGlobalAdmin || isOrgAdmin ? children : <Navigate to="/" replace />;
};

// Redirects a marketing variant slug to its canonical path for the current host
// (clean "/<slug>" on voxpont.com, "/voxpont/<slug>" on the app domains).
const VoxPontMarketingSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={marketingVariantPath(slug)} replace />;
};

const FusionMsiMarketingSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={fusionMsiMarketingPagePath(slug)} replace />;
};

const FusionMsiLegacyRedirect = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

const FusionMsiRecordEditorRedirect = () => {
  const { type, recordId } = useParams();
  return <Navigate to={fusionMsiRecordEditorPath(type, recordId)} replace />;
};

/**
 * Public-computer idle guard for standalone patient routes (not PatientRoutes shell).
 * Exact `/visit-start`, `/discharge`, `/voxpont12`; `/carestart` + variants; `/carestart-plus*`.
 */
function shouldMountPublicComputerIdleGuard(pathname) {
  const path = String(pathname || "").replace(/\/$/, "") || "/";
  if (path === "/visit-start") return true;
  if (path === "/discharge") return true;
  if (path === "/voxpont12") return true;
  if (path === "/carestart" || path.startsWith("/carestart/")) return true;
  if (path === "/carestart-plus" || path.startsWith("/carestart-plus/")) {
    return true;
  }
  return false;
}

function AppPublicComputerIdleGuard() {
  const { pathname } = useLocation();
  if (!shouldMountPublicComputerIdleGuard(pathname)) return null;
  return <PublicComputerIdleGuard />;
}

export const AppRoutes = () => {
  const { user, userLoading } = useAuth();
  const signedOutLocation = useLocation();
  // On the marketing host (voxpont.com) the marketing splash is served at the site root with
  // clean per-variant paths; on the app domains it lives under /voxpont. Host is fixed for the
  // page load, so computing it once here is sufficient.
  const marketingHost = isVoxPontMarketingHost();
  const fusionMsiHost = isFusionMsiHost();
  const healthContoursHost = isHealthContoursHost();
  const clinicalHudHost = isClinicalHudMarketingHost();
  const md3cHost = isMd3cMarketingHost();
  const msi3Host = isMsi3Host();
  const asiMarketingHost = isAsiMarketingHost();
  const productRedirects = liveProductPathRedirects();

  const clinicalHudSlugElement = (slug) => {
    if (slug === "how-it-works") {
      return <ClinicalHudMarketingHowItWorksPage />;
    }
    if (slug === "ddx-vs-pearls") {
      return <ClinicalHudMarketingDdxVsPearlsPage />;
    }
    if (slug === "request-access") {
      return <ClinicalHudMarketingRequestAccessPage />;
    }
    if (slug === CLINICALHUD_OVERVIEW_SLUG) {
      return <ClinicalHudMarketingHubPage />;
    }
    return <ClinicalHudMarketingSplashPage />;
  };

  const asiMarketingSlugElement = (slug) => {
    if (slug === "method" || slug === "how-it-works") {
      return <AsiMarketingMethodPage />;
    }
    if (slug === "learners") {
      return <AsiMarketingLearnersPage />;
    }
    if (slug === "faculty") {
      return <AsiMarketingFacultyPage />;
    }
    if (slug === "apps") {
      return <AsiMarketingAppsPage />;
    }
    if (slug === "contact") {
      return <AsiMarketingContactPage />;
    }
    if (slug === ASI_OVERVIEW_SLUG) {
      return <AsiMarketingHubPage />;
    }
    return <AsiMarketingSplashPage />;
  };

  if (userLoading) return <Loading page />;
  if (md3cHost && !isSignedOutEntryPath(signedOutLocation.pathname)) {
    return (
      <Suspense fallback={<Loading page />}>
        <Md3cNotFoundPage />
      </Suspense>
    );
  }
  if (!user && !isSignedOutEntryPath(signedOutLocation.pathname)) {
    return (
      <Navigate
        to={signedOutSignInTarget({
          pathname: signedOutLocation.pathname,
          search: signedOutLocation.search,
          hostname: typeof window !== "undefined" ? window.location.hostname : "",
        })}
        replace
      />
    );
  }

  return (
    <>
      <AppPublicComputerIdleGuard />
      <ProductHostEntry />
      <Routes>
      {isDevEnvironment() ? (
        <Route path="/fusion-msi-wait-preview" element={<FusionMsiWaitPreviewPage />} />
      ) : null}
      {/* Marketing host only: clean per-domain variant paths (the splash itself is the "/" route below). */}
      {marketingHost &&
        MARKETING_SLUGS.map((slug) => (
          <Route
            key={slug}
            path={`/${slug}`}
            element={
              <Suspense fallback={<Loading page />}>
                <VoxPontMarketingPage slug={slug} />
              </Suspense>
            }
          />
        ))}
      {/* ClinicalHUD dedicated host: clean /overview, /how-it-works, /ddx-vs-pearls, /request-access. */}
      {clinicalHudHost &&
        CLINICALHUD_MARKETING_SLUGS.map((slug) => (
          <Route
            key={`clinicalhud-host-${slug}`}
            path={`/${slug}`}
            element={
              <Suspense fallback={<Loading page />}>
                {clinicalHudSlugElement(slug)}
              </Suspense>
            }
          />
        ))}
      {/* ClinicalHUD on app hosts (and preview): /clinicalhud namespace. Splash at /clinicalhud. */}
      <Route
        path="/clinicalhud"
        element={
          <Suspense fallback={<Loading page />}>
            <ClinicalHudMarketingSplashPage />
          </Suspense>
        }
      />
      {CLINICALHUD_MARKETING_SLUGS.map((slug) => (
        <Route
          key={`clinicalhud-app-${slug}`}
          path={`/clinicalhud/${slug}`}
          element={
            <Suspense fallback={<Loading page />}>
              {clinicalHudSlugElement(slug)}
            </Suspense>
          }
        />
      ))}
      {msi3Host && (
        <>
          <Route
            path="/pricing"
            element={
              <Suspense fallback={<Loading page />}>
                <Msi3MarketingPricingPage />
              </Suspense>
            }
          />
          <Route
            path="/faculty"
            element={
              <Suspense fallback={<Loading page />}>
                <Msi3MarketingFacultyPage />
              </Suspense>
            }
          />
          <Route
            path="/faq"
            element={
              <Suspense fallback={<Loading page />}>
                <Msi3MarketingFaqPage />
              </Suspense>
            }
          />
          <Route
            path="/checkout/success"
            element={
              <Suspense fallback={<Loading page />}>
                <Msi3CheckoutSuccessPage />
              </Suspense>
            }
          />
          <Route
            path="/checkout/cancel"
            element={
              <Suspense fallback={<Loading page />}>
                <Msi3CheckoutCancelPage />
              </Suspense>
            }
          />
        </>
      )}
      {/* ASI dedicated host: clean /overview, /method, /learners, … */}
      {asiMarketingHost &&
        ASI_MARKETING_SLUGS.map((slug) => (
          <Route
            key={`asi-host-${slug}`}
            path={`/${slug}`}
            element={
              <Suspense fallback={<Loading page />}>
                {asiMarketingSlugElement(slug)}
              </Suspense>
            }
          />
        ))}
      {/* ASI on app hosts (and preview): /asi-marketing namespace. */}
      <Route
        path="/asi-marketing"
        element={
          <Suspense fallback={<Loading page />}>
            <AsiMarketingSplashPage />
          </Suspense>
        }
      />
      {ASI_MARKETING_SLUGS.map((slug) => (
        <Route
          key={`asi-app-${slug}`}
          path={`/asi-marketing/${slug}`}
          element={
            <Suspense fallback={<Loading page />}>
              {asiMarketingSlugElement(slug)}
            </Suspense>
          }
        />
      ))}
      {/* VoxPont brand assets board — clean "/brand" on voxpont.com; "/voxpont/brand"
          everywhere (static path outranks the "/voxpont/:slug" variant route). */}
      {marketingHost && (
        <Route
          path="/brand"
          element={
            <Suspense fallback={<Loading page />}>
              <VoxPontBrandPage />
            </Suspense>
          }
        />
      )}
      <Route
        path="/voxpont/brand"
        element={
          <Suspense fallback={<Loading page />}>
            <VoxPontBrandPage />
          </Suspense>
        }
      />
      {/* BYOI acquisition-channel landing — clean "/byoi" on voxpont.com;
          "/voxpont/byoi" everywhere (static path outranks the "/voxpont/:slug"
          variant route). CTA stamps accounts via ?origin=byoi. */}
      {marketingHost && (
        <Route
          path="/byoi"
          element={
            <Suspense fallback={<Loading page />}>
              <VoxPontByoiPage />
            </Suspense>
          }
        />
      )}
      <Route
        path="/voxpont/byoi"
        element={
          <Suspense fallback={<Loading page />}>
            <VoxPontByoiPage />
          </Suspense>
        }
      />
      {/* VoxPont marketing-materials reference — clean "/marketing-reference" on
          voxpont.com; "/voxpont/marketing-reference" everywhere (static path
          outranks the "/voxpont/:slug" variant route). */}
      {marketingHost && (
        <Route
          path="/marketing-reference"
          element={
            <Suspense fallback={<Loading page />}>
              <VoxPontMarketingReferencePage />
            </Suspense>
          }
        />
      )}
      <Route
        path="/voxpont/marketing-reference"
        element={
          <Suspense fallback={<Loading page />}>
            <VoxPontMarketingReferencePage />
          </Suspense>
        }
      />
    <Route
      path="/dashboard/*"
      element={
        <Elements stripe={stripePromise}>
          <LocationProvider>
            <DashboardPage />
          </LocationProvider>
        </Elements>
      }
    />

    {/* Domain-aware: VoxPont app domains get the branded page; HealthDesk keeps AuthPage.
        fusionmsi.com uses /auth inside DpLayout instead. */}
    {!fusionMsiHost && (
      <Route
        path="/auth"
        element={isVoxPontAppDomain() ? <VoxPontAuthPage /> : <AuthPage />}
      />
    )}
    {/* fusionmsi.com reserves /auth for the DecisionPoint invite sign-in, so the
        role-typed marketing signup CTAs land on the main-app registration here. */}
    {fusionMsiHost && <Route path="/signup" element={<AuthPage />} />}
    {/* Canonical VoxPont auth route — renders the branded page on any domain. */}
    <Route path="/voxpont-auth" element={<VoxPontAuthPage />} />
    {/* VoxPont post-auth landing: product picker, or redirect to saved product. */}
    <Route path="/voxpont-start" element={<VoxPontOnboardingPage />} />
    {/* VoxPont branded legal documents (linked from the auth-page agreement notice). */}
    <Route path="/voxpont-terms" element={<VoxPontLegalPage doc="terms" />} />
    <Route path="/voxpont-privacy" element={<VoxPontLegalPage doc="privacy" />} />
    <Route path="/voxpont-baa" element={<VoxPontLegalPage doc="baa" />} />
    {/* VoxPont Beta invite-claim landing (clicked from an invite link). */}
    <Route path="/voxpont-beta-join" element={<VoxPontBetaJoinPage />} />
    <Route
      path="/cities1"
      element={
        <Suspense fallback={<Loading page />}>
          <Cities1Page />
        </Suspense>
      }
    />
    {/* DecisionPoint / FusionMSI — pathless layout; fusionmsi.com uses clean root paths. */}
    <Route element={<DpLayout />}>
      <Route
        path="/flash13"
        element={
          <Suspense fallback={null}>
            <FusionMsiMarketingHubPage />
          </Suspense>
        }
      />
      {/* Same audience-gate splash as fusionmsi.com `/` (and `/dp` on app hosts).
          No full-page wait here: the aperture intro is the page-load animation. */}
      <Route
        path="/splashpage"
        element={
          <Suspense fallback={null}>
            <FusionMsiMarketingHubPage />
          </Suspense>
        }
      />
      {fusionMsiHost ? (
        <>
          <Route
            path="/"
            element={
              <Suspense fallback={null}>
                <RoleHubGate />
              </Suspense>
            }
          />
          {FUSION_MSI_MARKETING_SLUGS.map((slug) => (
            <Route
              key={slug}
              path={`/${slug}`}
              element={
                <Suspense fallback={<Loading page />}>
                  <FusionMsiMarketingSubPage />
                </Suspense>
              }
            />
          ))}
          {FUSION_MSI_AUDIENCE_SLUGS.map((slug) => (
            <Route
              key={`audience-${slug}`}
              path={`/${slug}`}
              element={
                <Suspense fallback={<Loading page />}>
                  <FusionMsiAudiencePage slug={slug} />
                </Suspense>
              }
            />
          ))}
          <Route
            path="/about"
            element={
              <Suspense fallback={<Loading page />}>
                <FusionMsiAboutPage />
              </Suspense>
            }
          />
          <Route
            path="/business-model"
            element={
              <Suspense fallback={<Loading page />}>
                <FusionMsiBusinessModelPage />
              </Suspense>
            }
          />
          <Route
            path="/carestart-sponsor"
            element={
              <Suspense fallback={<Loading page />}>
                <CareStartSponsorPage />
              </Suspense>
            }
          />
          <Route
            path="/get-on-the-map"
            element={
              <Suspense fallback={<Loading page />}>
                <FusionMsiGetOnTheMapPage />
              </Suspense>
            }
          />
          <Route
            path="/historical-precedent"
            element={
              <Suspense fallback={<Loading page />}>
                <HistoricalPrecedentPage />
              </Suspense>
            }
          />
          <Route
            path="/platform/*"
            element={
              <Suspense fallback={<Loading page />}>
                <PlatformRouter />
              </Suspense>
            }
          />
          {FUSION_MSI_PLATFORM_SLUGS.map((slug) => (
            <Route
              key={`platform-${slug}`}
              path={`/platform/${slug}`}
              element={
                <Suspense fallback={<Loading page />}>
                  <PlatformRouter />
                </Suspense>
              }
            />
          ))}
          <Route
            path="/rollout/:linkCode"
            element={
              <Suspense fallback={<Loading page />}>
                <ChampionVideoRolloutPage />
              </Suspense>
            }
          />
          {fusionMsiRoleSplashRouteElements(<UnmatchedRouteFallback />)}
          {productRedirects.map((row) => (
            <Route
              key={`product-subdomain-${row.path}`}
              path={row.path}
              element={<ProductSubdomainReplace url={row.url} />}
            />
          ))}
          <Route
            path="/:country/:city"
            element={
              <Suspense fallback={<Loading page />}>
                <CityMarketingPage />
              </Suspense>
            }
          />
          <Route
            path="/:country"
            element={
              <Suspense fallback={<Loading page />}>
                <CountryMarketingPage />
              </Suspense>
            }
          />
          <Route path="/dp" element={<Navigate to="/" replace />} />
          <Route path="/dp/:slug" element={<FusionMsiMarketingSlugRedirect />} />
          <Route path="/dp-auth" element={<FusionMsiLegacyRedirect to="/auth" />} />
          <Route path="/dp-join" element={<FusionMsiLegacyRedirect to="/join" />} />
          <Route path="/dp-display" element={<FusionMsiLegacyRedirect to="/display" />} />
          <Route path="/dp-admin" element={<FusionMsiLegacyRedirect to="/admin" />} />
          <Route
            path="/dp-admin/records/:type/:recordId"
            element={<FusionMsiRecordEditorRedirect />}
          />
          <Route path="/dp-admin/import" element={<FusionMsiLegacyRedirect to="/admin/import" />} />
          <Route path="/dp-admin/access" element={<FusionMsiLegacyRedirect to="/admin/access" />} />
          <Route
            path="/dp-admin/reviewers"
            element={<FusionMsiLegacyRedirect to="/admin/reviewers" />}
          />
          <Route
            path="/dp-admin/coverage"
            element={<FusionMsiLegacyRedirect to="/admin/coverage" />}
          />
          <Route path="/dp-admin/audit" element={<FusionMsiLegacyRedirect to="/admin/audit" />} />
          <Route path="/dp-review" element={<FusionMsiLegacyRedirect to="/review" />} />
          <Route path="/dp-sources" element={<FusionMsiLegacyRedirect to="/sources" />} />
          <Route
            path="/dp-admin/library"
            element={<FusionMsiLegacyRedirect to="/admin/library" />}
          />
          <Route
            path="/auth"
            element={
              <Suspense fallback={<Loading page />}>
                <DpAuthPage />
              </Suspense>
            }
          />
          <Route
            path="/join"
            element={
              <Suspense fallback={<Loading page />}>
                <DpJoinPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/access"
            element={
              <RequireDpGlobalAdmin>
                <Suspense fallback={<Loading page />}>
                  <DpAccessRosterPage />
                </Suspense>
              </RequireDpGlobalAdmin>
            }
          />
          <Route
            path="/admin/reviewers"
            element={
              <RequireDpGlobalAdmin>
                <Suspense fallback={<Loading page />}>
                  <DpReviewerAdminPage />
                </Suspense>
              </RequireDpGlobalAdmin>
            }
          />
          <Route
            path="/admin/coverage"
            element={
              <RequireDpGlobalAdmin>
                <Suspense fallback={<Loading page />}>
                  <DpCoveragePage />
                </Suspense>
              </RequireDpGlobalAdmin>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <RequireDpGlobalAdmin>
                <Suspense fallback={<Loading page />}>
                  <DpAuditPage />
                </Suspense>
              </RequireDpGlobalAdmin>
            }
          />
          <Route element={<DpRouteGate />}>
            <Route
              path="/display"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpDisplayPage />
                </Suspense>
              }
            />
            <Route
              path="/admin"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpAdminHomePage />
                </Suspense>
              }
            />
            <Route
              path="/admin/records/:type/:recordId"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpRecordEditorPage />
                </Suspense>
              }
            />
            <Route
              path="/admin/import"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpImportPage />
                </Suspense>
              }
            />
            <Route
              path="/admin/library"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpLibraryPage />
                </Suspense>
              }
            />
            <Route
              path="/sources"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpContentSourcesPage />
                </Suspense>
              }
            />
            <Route
              path="/review"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpReviewPage />
                </Suspense>
              }
            />
          </Route>
        </>
      ) : (
        <>
          <Route
            path="/dp/platform/*"
            element={
              <Suspense fallback={<Loading page />}>
                <PlatformRouter />
              </Suspense>
            }
          />
          <Route
            path="/dp/*"
            element={
              <Suspense fallback={<Loading page />}>
                <FusionMsiMarketingRouter />
              </Suspense>
            }
          />
          <Route
            path="/dp-auth"
            element={
              <Suspense fallback={<Loading page />}>
                <DpAuthPage />
              </Suspense>
            }
          />
          <Route
            path="/dp-join"
            element={
              <Suspense fallback={<Loading page />}>
                <DpJoinPage />
              </Suspense>
            }
          />
          <Route
            path="/dp-admin/access"
            element={
              <RequireDpGlobalAdmin>
                <Suspense fallback={<Loading page />}>
                  <DpAccessRosterPage />
                </Suspense>
              </RequireDpGlobalAdmin>
            }
          />
          <Route
            path="/dp-admin/reviewers"
            element={
              <RequireDpGlobalAdmin>
                <Suspense fallback={<Loading page />}>
                  <DpReviewerAdminPage />
                </Suspense>
              </RequireDpGlobalAdmin>
            }
          />
          <Route
            path="/dp-admin/coverage"
            element={
              <RequireDpGlobalAdmin>
                <Suspense fallback={<Loading page />}>
                  <DpCoveragePage />
                </Suspense>
              </RequireDpGlobalAdmin>
            }
          />
          <Route
            path="/dp-admin/audit"
            element={
              <RequireDpGlobalAdmin>
                <Suspense fallback={<Loading page />}>
                  <DpAuditPage />
                </Suspense>
              </RequireDpGlobalAdmin>
            }
          />
          <Route element={<DpRouteGate />}>
            <Route
              path="/dp-display"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpDisplayPage />
                </Suspense>
              }
            />
            <Route
              path="/dp-admin"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpAdminHomePage />
                </Suspense>
              }
            />
            <Route
              path="/dp-admin/records/:type/:recordId"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpRecordEditorPage />
                </Suspense>
              }
            />
            <Route
              path="/dp-admin/import"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpImportPage />
                </Suspense>
              }
            />
            <Route
              path="/dp-admin/library"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpLibraryPage />
                </Suspense>
              }
            />
            <Route
              path="/dp-sources"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpContentSourcesPage />
                </Suspense>
              }
            />
            <Route
              path="/dp-review"
              element={
                <Suspense fallback={<Loading page />}>
                  <DpReviewPage />
                </Suspense>
              }
            />
          </Route>
        </>
      )}
    </Route>
    {/* Public institutional BAA request form — no auth (linked from marketing). */}
    <Route path="/voxpont-baa-request" element={<VoxPontBaaRequestPage />} />
    {/* DocuSign embedded-signing return landing — no auth. */}
    <Route path="/voxpont-baa-request/signed" element={<VoxPontBaaSignedPage />} />
    {/* VoxPont Beta admin dashboard — admin custom claim only. */}
    <Route
      path="/admin/voxpont-beta"
      element={
        <RequireGlobalAdmin>
          <AdminVoxPontBetaPage />
        </RequireGlobalAdmin>
      }
    />
    {/* BAA compliance dashboard — admin custom claim only (list + detail). */}
    <Route
      path="/admin/baa/requests"
      element={
        <RequireGlobalAdmin>
          <AdminBAAPage />
        </RequireGlobalAdmin>
      }
    />
    <Route
      path="/admin/baa/requests/:requestId"
      element={
        <RequireGlobalAdmin>
          <AdminBAAPage />
        </RequireGlobalAdmin>
      }
    />
    {/* SpecialtyKB reviewer invite — public. Creates a plain account, then claims. */}
    <Route
      path={SPECIALTY_KB_JOIN_PATH}
      element={
        <Suspense fallback={<Loading page />}>
          <SpecialtyKbReviewerInviteJoinPage />
        </Suspense>
      }
    />
    <Route
      path={SPECIALTY_REVIEWER_JOIN_PATH}
      element={
        <Suspense fallback={<Loading page />}>
          <SpecialtyKbReviewerInviteJoinPage />
        </Suspense>
      }
    />
    {/* SpecialtyKB Editors Dashboard — admin, or an assigned specialty reviewer. */}
    <Route
      path="/specialty-kb/editors"
      element={
        <RequireSpecialtyKbEditor>
          <Suspense fallback={<Loading page />}>
            <SpecialtyKbEditorsDashboardPage />
          </Suspense>
        </RequireSpecialtyKbEditor>
      }
    />
    <Route
      path="/specialty-kb/editors/:topicId"
      element={
        <RequireSpecialtyKbEditor>
          <Suspense fallback={<Loading page />}>
            <SpecialtyKbEditorsDashboardPage />
          </Suspense>
        </RequireSpecialtyKbEditor>
      }
    />
    {/* SpecialtyKB Differential Diagnosis — admin or clinicalHudAccess. */}
    <Route
      path="/specialty-kb/ddx"
      element={
        <RequireClinicalHudAccess>
          <Suspense fallback={<Loading page />}>
            <SpecialtyKbDifferentialDiagnosisPage />
          </Suspense>
        </RequireClinicalHudAccess>
      }
    />
    {/* ClinicalHUD product app — hyphenated path; marketing stays /clinicalhud*. */}
    <Route
      path="/clinical-hud"
      element={
        <RequireClinicalHudAccess>
          <Suspense fallback={<Loading page />}>
            <ClinicalHudPage />
          </Suspense>
        </RequireClinicalHudAccess>
      }
    />
    <Route
      path="/clinical-hud-2"
      element={
        <RequireClinicalHudAccess>
          <Suspense fallback={<Loading page />}>
            <ClinicalHud2ListPage />
          </Suspense>
        </RequireClinicalHudAccess>
      }
    />
    <Route
      path="/clinical-hud-2/:sessionId"
      element={
        <RequireClinicalHudAccess>
          <Suspense fallback={<Loading page />}>
            <ClinicalHud2WorkspacePage />
          </Suspense>
        </RequireClinicalHudAccess>
      }
    />
    <Route
      path="/chartmind-7"
      element={
        <RequireClinicalHudAccess>
          <Suspense fallback={<Loading page />}>
            <ChartMind7ListPage />
          </Suspense>
        </RequireClinicalHudAccess>
      }
    />
    <Route
      path="/chartmind-7/:sessionId"
      element={
        <RequireClinicalHudAccess>
          <Suspense fallback={<Loading page />}>
            <ChartMind7WorkspacePage />
          </Suspense>
        </RequireClinicalHudAccess>
      }
    />
    <Route
      path="/clinical-hud-3"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireClinicalHud3Teaching>
            <Suspense fallback={<Loading page />}>
              <ClinicalHud3TeachingListPage />
            </Suspense>
          </RequireClinicalHud3Teaching>
        </ClinicalHud3SettingsProvider>
      }
    />
    <Route
      path="/clinical-hud-3/admin/cases"
      element={
        <RequireGlobalAdmin>
          <Suspense fallback={<Loading page />}>
            <ClinicalHud3AdminCasesPage />
          </Suspense>
        </RequireGlobalAdmin>
      }
    />
    <Route
      path="/clinical-hud-3/admin/usage"
      element={
        <RequireGlobalAdmin>
          <Suspense fallback={<Loading page />}>
            <ClinicalHud3AdminUsagePage />
          </Suspense>
        </RequireGlobalAdmin>
      }
    />
    <Route
      path="/clinical-hud-3/case/:sessionId"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireClinicalHud3Teaching>
            <Suspense fallback={<Loading page />}>
              <ClinicalHud3WorkspacePage config={CLINICAL_HUD3_TEACHING} />
            </Suspense>
          </RequireClinicalHud3Teaching>
        </ClinicalHud3SettingsProvider>
      }
    />
    <Route
      path="/clinical-hud-3/live"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireClinicalHud3LiveAccess>
            <Suspense fallback={<Loading page />}>
              <ClinicalHud3LiveListPage />
            </Suspense>
          </RequireClinicalHud3LiveAccess>
        </ClinicalHud3SettingsProvider>
      }
    />
    <Route
      path="/clinical-hud-3/live/:sessionId"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireClinicalHud3LiveAccess>
            <Suspense fallback={<Loading page />}>
              <ClinicalHud3WorkspacePage config={CLINICAL_HUD3_LIVE} />
            </Suspense>
          </RequireClinicalHud3LiveAccess>
        </ClinicalHud3SettingsProvider>
      }
    />
    <Route
      path="/clinical-hud-4"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireClinicalHud3Teaching>
            <Suspense fallback={<Loading page />}>
              <ClinicalHud4TeachingListPage />
            </Suspense>
          </RequireClinicalHud3Teaching>
        </ClinicalHud3SettingsProvider>
      }
    />
    <Route
      path="/clinical-hud-4/case/:sessionId"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireClinicalHud3Teaching>
            <Suspense fallback={<Loading page />}>
              <ClinicalHud3WorkspacePage config={CLINICAL_HUD4_TEACHING} />
            </Suspense>
          </RequireClinicalHud3Teaching>
        </ClinicalHud3SettingsProvider>
      }
    />
    <Route
      path="/clinical-hud-5"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireMsi3Learner>
            <Suspense fallback={<Loading page />}>
              <ClinicalHud5TeachingListPage />
            </Suspense>
          </RequireMsi3Learner>
        </ClinicalHud3SettingsProvider>
      }
    />
    <Route
      path="/clinical-hud-5/case/:sessionId"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireMsi3Learner>
            <Suspense fallback={<Loading page />}>
              <ClinicalHud3WorkspacePage config={CLINICAL_HUD5_TEACHING} />
            </Suspense>
          </RequireMsi3Learner>
        </ClinicalHud3SettingsProvider>
      }
    />
    {/* Applied Superintelligence teaching apps — FactPattern / RadioCall */}
    <Route
      path="/asi/teaching"
      element={
        <RequireMsi3Learner>
          <Suspense fallback={<Loading page />}>
            <AsiTeachingLauncherPage />
          </Suspense>
        </RequireMsi3Learner>
      }
    />
    <Route
      path="/fact-pattern"
      element={
        <RequireMsi3Learner>
          <Suspense fallback={<Loading page />}>
            <FactPatternListPage />
          </Suspense>
        </RequireMsi3Learner>
      }
    />
    <Route
      path="/fact-pattern/case/:caseId"
      element={
        <RequireMsi3Learner>
          <Suspense fallback={<Loading page />}>
            <FactPatternSessionPage />
          </Suspense>
        </RequireMsi3Learner>
      }
    />
    <Route
      path="/fact-pattern/faculty/cases"
      element={
        <RequireMsi3Faculty>
          <Suspense fallback={<Loading page />}>
            <FactPatternFacultyPage />
          </Suspense>
        </RequireMsi3Faculty>
      }
    />
    <Route
      path="/radio-call"
      element={
        <RequireMsi3Learner>
          <Suspense fallback={<Loading page />}>
            <RadioCallListPage />
          </Suspense>
        </RequireMsi3Learner>
      }
    />
    <Route
      path="/radio-call/case/:caseId"
      element={
        <RequireMsi3Learner>
          <Suspense fallback={<Loading page />}>
            <RadioCallSessionPage />
          </Suspense>
        </RequireMsi3Learner>
      }
    />
    <Route
      path="/radio-call/faculty/cases"
      element={
        <RequireMsi3Faculty>
          <Suspense fallback={<Loading page />}>
            <RadioCallFacultyPage />
          </Suspense>
        </RequireMsi3Faculty>
      }
    />
    <Route
      path="/msi3/faculty/cases"
      element={
        <RequireMsi3Faculty>
          <Suspense fallback={<Loading page />}>
            <Msi3FacultyCasesPage />
          </Suspense>
        </RequireMsi3Faculty>
      }
    />
    {/* MSI3 learner progress — own Emergency Medicine topic mastery. */}
    <Route
      path="/msi3/progress"
      element={
        <ClinicalHud3SettingsProvider>
          <RequireMsi3Learner>
            <Suspense fallback={<Loading page />}>
              <Msi3LearnerProgressPage />
            </Suspense>
          </RequireMsi3Learner>
        </ClinicalHud3SettingsProvider>
      }
    />
    {/* MSI3 org progress — faculty grant, org-admin, or global admin (own org). */}
    <Route
      path="/msi3/org/progress"
      element={
        <RequireMsi3OrgProgress>
          <Suspense fallback={<Loading page />}>
            <Msi3OrgProgressPage />
          </Suspense>
        </RequireMsi3OrgProgress>
      }
    />
    <Route
      path="/msi3/admin/tags"
      element={
        <RequireGlobalAdmin>
          <Suspense fallback={<Loading page />}>
            <Msi3AdminTagsPage />
          </Suspense>
        </RequireGlobalAdmin>
      }
    />
    {/* MSI3 access & pricing config — global admin only. */}
    <Route
      path="/msi3/admin/access"
      element={
        <RequireGlobalAdmin>
          <Suspense fallback={<Loading page />}>
            <Msi3AdminAccessPage />
          </Suspense>
        </RequireGlobalAdmin>
      }
    />
    {/* MSI3 faculty invite — public. Creates/signs in an account, then claims. */}
    <Route
      path="/msi3/faculty/join"
      element={
        <Suspense fallback={<Loading page />}>
          <Msi3FacultyJoinPage />
        </Suspense>
      }
    />
    {/* MSI3 school seat assignment — global admin or a school org-admin. */}
    <Route
      path="/msi3/org/seats"
      element={
        <RequireMsi3SeatManager>
          <Suspense fallback={<Loading page />}>
            <Msi3OrgSeatsPage />
          </Suspense>
        </RequireMsi3SeatManager>
      }
    />
    <Route
      path="/msi3/org/cases"
      element={
        <RequireMsi3SeatManager>
          <Suspense fallback={<Loading page />}>
            <Msi3OrgGlobalCasesPage />
          </Suspense>
        </RequireMsi3SeatManager>
      }
    />
    <Route
      path="/chartmind-i"
      element={
        <RequireChartMindIAccess>
          <Suspense fallback={<Loading page />}>
            <ChartMindIRosterPage />
          </Suspense>
        </RequireChartMindIAccess>
      }
    />
    <Route
      path="/chartmind-i/launch"
      element={
        <RequireChartMindIAccess>
          <Suspense fallback={<Loading page />}>
            <ChartMindILaunchPage />
          </Suspense>
        </RequireChartMindIAccess>
      }
    />
    <Route
      path="/chartmind-i/:admissionId"
      element={
        <RequireChartMindIAccess>
          <Suspense fallback={<Loading page />}>
            <ChartMindIWorkspacePage />
          </Suspense>
        </RequireChartMindIAccess>
      }
    />
    <Route
      path="/chartmind-i/:admissionId/notes/:noteId"
      element={
        <RequireChartMindIAccess>
          <Suspense fallback={<Loading page />}>
            <ChartMindINotePage />
          </Suspense>
        </RequireChartMindIAccess>
      }
    />
    <Route
      path="/chartmind-i2"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2RosterPage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route
      path="/chartmind-i2/launch"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2LaunchPage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route
      path="/chartmind-i2/todos"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2MyTodosPage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route
      path="/chartmind-i2/teams"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2TeamsPage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route
      path="/chartmind-i2/teams/:teamId/settings"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2TeamSettingsPage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route
      path="/chartmind-i2/teams/:teamId"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2TeamCensusPage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route
      path="/chartmind-i2/join"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2JoinTeamPage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route
      path="/chartmind-i2/:admissionId"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2WorkspacePage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route
      path="/chartmind-i2/:admissionId/notes/:noteId"
      element={
        <RequireChartMindI2Access>
          <Suspense fallback={<Loading page />}>
            <ChartMindI2NotePage />
          </Suspense>
        </RequireChartMindI2Access>
      }
    />
    <Route path="/login" element={<Navigate to="/auth" replace />} />
    
    {/* Organization Invitation */}
    <Route path="/join/:token" element={<JoinPage />} />

    {/* Public clinic check-in landing (scanned QR / shared link) */}
    <Route path="/checkin/:clinicId" element={<CheckInPage />} />

    {/* Public landing for a patient-shared medical record (scanned QR) */}
    <Route path="/share" element={<ShareLandingPage />} />
    <Route
      path="/print/ai-listening-sign"
      element={
        <Suspense fallback={<Loading page />}>
          <AiListeningSignPage />
        </Suspense>
      }
    />
    {/* Public SecureLink — Discharge Import (ehr-discharge QR / URL) */}
    <Route
      path="/discharge"
      element={
        <Suspense fallback={<Loading page />}>
          <EhrDischargeLandingPage />
        </Suspense>
      }
    />
    <Route
      path="/tci-session-consent"
      element={
        <Suspense fallback={<Loading page />}>
          <TciSessionConsentMintPage />
        </Suspense>
      }
    />

    {/* Public champion rollout video share link */}
    <Route path="/rollout/:linkCode" element={<ChampionVideoRolloutPage />} />

    {/* Legacy registration routes - redirect to new auth with role param */}
    <Route path="/register" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/register/patient" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/register/provider" element={<Navigate to="/auth?role=provider" replace />} />
    <Route path="/register/facility" element={<Navigate to="/auth?role=facility" replace />} />
    <Route path="/msi/register" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/pro/register" element={<Navigate to="/auth?role=provider" replace />} />
    <Route path="/facility/register" element={<Navigate to="/auth?role=facility" replace />} />

    {/* Other auth routes */}
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/wp-auth-handler" element={<WPAuthHandler />} />

    {/* Account Routes */}
    <Route path="/account" element={<Verify />} />
    <Route path="/onboarding" element={<Onboarding />} />

    {/* Public VoxPont marketing splash — hub + per-domain variant pages (voxpont.com landing).
        On the marketing host these canonicalize to clean root paths ("/" and "/<slug>"). */}
    <Route
      path="/voxpont"
      element={
        marketingHost ? (
          <Navigate to="/" replace />
        ) : (
          <Suspense fallback={<Loading page />}>
            <VoxPontMarketingPage />
          </Suspense>
        )
      }
    />
    <Route
      path="/voxpont/:slug"
      element={
        marketingHost ? (
          <VoxPontMarketingSlugRedirect />
        ) : (
          <Suspense fallback={<Loading page />}>
            <VoxPontMarketingPage />
          </Suspense>
        )
      }
    />
    {/* Legacy URLs: /voxpontmarketing was renamed to /voxpont. */}
    <Route path="/voxpontmarketing" element={<Navigate to={marketingHubPath()} replace />} />
    <Route
      path="/voxpontmarketing/:slug"
      element={<VoxPontMarketingSlugRedirect />}
    />

    {/* VoxPont2 — standalone live-translation voice session (beta) */}
    <Route
      path="/voxpont2"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont2Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    {/* Legacy URL: /carevoice2 was renamed to /voxpont2. */}
    <Route path="/carevoice2" element={<Navigate to="/voxpont2" replace />} />

    {/* CareCompris — clinical AI translation. /carevoice is backed by CareCompris2;
        not yet surfaced in the UI. Auth handled inside each page. */}
    <Route
      path="/carecompris"
      element={
        <Suspense fallback={<Loading page />}>
          <CareComprisPage />
        </Suspense>
      }
    />
    <Route
      path="/carevoice"
      element={
        <Suspense fallback={<Loading page />}>
          <CareCompris2Page />
        </Suspense>
      }
    />
    {/* Legacy URL: /carecompris2 was renamed to /carevoice. */}
    <Route path="/carecompris2" element={<Navigate to="/carevoice" replace />} />

    {/* VoxPont forks 3/4 and the 10–70 domain family (not yet surfaced in the UI). */}
    <Route
      path="/voxpont3"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont3Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont4"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont4Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont12"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont12Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont12/audio-lab"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont12AudioLabPage />
        </Suspense>
      }
    />
    <Route
      path="/voxpont5"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont5Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont201"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont201Page />
        </Suspense>
      }
    />
    {/* voxpont203: feature folder exists (src/features/voxpont203/) but is
        intentionally unrouted — R&D surface, do not treat as dead code.
        Do not add /voxpont203 without owner sign-off. */}
    <Route
      path="/VoxPont204"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont204Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont301"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont301Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont-offline"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontOfflineLandingPage />
        </Suspense>
      }
    />
    <Route
      path="/voxpont501"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont501Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont302"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont302Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont204"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont204Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont205"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont205Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont700"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont700Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont202"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont202Page />
        </Suspense>
      }
    />
    <Route
      path="/voxpont20"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont20Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont30"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont30Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont40"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont40Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont50"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont50Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont60"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont60Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont70"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont70Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont100"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPont100Page />
        </Suspense>
      }
    />

    {/* Experimental VP110–VP430 — shared paired shell, beta-gated */}
    <Route
      path="/voxpont110"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont110Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont120"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont120Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont130"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont130Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont140"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont140Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont150"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont150Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont160"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont160Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont170"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont170Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont180"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont180Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont190"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont190Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont200"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont200Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont210"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont210Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont220"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont220Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont230"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont230Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont240"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont240Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont250"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont250Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont260"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont260Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont270"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont270Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont280"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont280Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont290"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont290Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont300"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont300Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont310"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont310Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont320"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont320Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont330"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont330Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont340"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont340Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont350"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont350Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont360"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont360Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont370"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont370Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont380"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont380Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont390"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont390Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont400"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont400Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont410"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont410Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont420"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont420Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />
    <Route
      path="/voxpont430"
      element={
        <Suspense fallback={<Loading page />}>
          <VoxPontBetaRouteGate>
            <VoxPont430Page />
          </VoxPontBetaRouteGate>
        </Suspense>
      }
    />

    {/* Legacy URLs: /carevoiceN routes were renamed to /voxpontN. */}
    <Route path="/carevoice3" element={<Navigate to="/voxpont3" replace />} />
    <Route path="/carevoice4" element={<Navigate to="/voxpont4" replace />} />
    <Route path="/carevoice12" element={<Navigate to="/voxpont12" replace />} />
    <Route path="/carevoice5" element={<Navigate to="/voxpont5" replace />} />
    <Route path="/carevoice20" element={<Navigate to="/voxpont20" replace />} />
    <Route path="/carevoice30" element={<Navigate to="/voxpont30" replace />} />
    <Route path="/carevoice40" element={<Navigate to="/voxpont40" replace />} />
    <Route path="/carevoice50" element={<Navigate to="/voxpont50" replace />} />
    <Route path="/carevoice60" element={<Navigate to="/voxpont60" replace />} />
    <Route path="/carevoice70" element={<Navigate to="/voxpont70" replace />} />
    <Route path="/carevoice100" element={<Navigate to="/voxpont100" replace />} />

    {/* CareStart3 — multilingual CareStart (consent -> voice interview -> DDX -> CareMap) */}
    <Route
      path="/carestart3"
      element={
        <Suspense fallback={<Loading page />}>
          <LocationProvider>
            <CareStart3Page />
          </LocationProvider>
        </Suspense>
      }
    />
    <Route
      path="/carestart3/:variantSlug"
      element={
        <Suspense fallback={<Loading page />}>
          <LocationProvider>
            <CareStartPublicSlugPage experience="carestart3" />
          </LocationProvider>
        </Suspense>
      }
    />

    {/* CareStart-Plus modules */}
    <Route
      path="/carestart-plus"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="carestart-plus">
            <CareStartPlusPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/carestart-plus/registration"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="carestart-plus">
            <CareStartPlusRegistrationPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/carestart-plus/triage"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="carestart-plus">
            <CareStartPlusTriagePage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/carestart-plus/hpi"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="carestart-plus">
            <CareStartPlusHpiPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/carestart-plus/discharge"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="carestart-plus">
            <CareStartPlusDischargePage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/visit-start"
      element={
        <Suspense fallback={<Loading page />}>
          <LocationProvider>
            <VisitStartPage />
          </LocationProvider>
        </Suspense>
      }
    />
    <Route
      path="/visit-start/governance"
      element={
        <Suspense fallback={<Loading page />}>
          <LocationProvider>
            <VisitStartAdminGovernancePage />
          </LocationProvider>
        </Suspense>
      }
    />

    {/* ChartMind-Plus — public path uses the same launcher catalog as the clinician tile.
        Dashboard copies in ProfessionalRoutes stay unchanged. */}
    <Route
      path="/chartmind-plus"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="chartmind-plus">
            <ChartMindPlusPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/chartmind-plus/differential"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="chartmind-plus">
            <ChartMindPlusDifferentialPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />

    {/* CareStart public — English default + help + city pages and facility variants.
        City slugs (England and Kenya skyline list) are resolved inside
        CareStartPublicSlugPage before a facility variant lookup. */}
    <Route
      path="/carestart"
      element={
        <Suspense fallback={<Loading page />}>
          <LocationProvider>
            <Scmap4Page />
          </LocationProvider>
        </Suspense>
      }
    />
    <Route
      path="/carestart/help"
      element={
        <Suspense fallback={<Loading page />}>
          <Scmap4HelpPage />
        </Suspense>
      }
    />
    <Route
      path="/carestart/:variantSlug"
      element={
        <Suspense fallback={<Loading page />}>
          <LocationProvider>
            <CareStartPublicSlugPage />
          </LocationProvider>
        </Suspense>
      }
    />

    {/* CareVerify — standalone booking (not gated behind CareStart). */}
    <Route
      path="/careverify"
      element={
        <Suspense fallback={<Loading page />}>
          <VisitTypeSelfDesignationPage />
        </Suspense>
      }
    />
    <Route
      path="/careverify/book"
      element={
        <Suspense fallback={<Loading page />}>
          <CareVerifyBookingPage />
        </Suspense>
      }
    />

    {/* CareMap-1 — standalone public facility map (formerly orphaned MapPage).
        Reads the same `locations` data / renders the same <Map> component as
        CareStart's embedded CareMap-2. Public: `locations` is public-read. */}
    {/* CareMap and CareMap2 play the star intro themselves. A Fusion wait
        overlay here would run that same animation twice. */}
    <Route
      path="/caremap"
      element={
        <Suspense fallback={null}>
          <MapPage />
        </Suspense>
      }
    />
    <Route
      path="/caremap2"
      element={
        <Suspense fallback={null}>
          <CareMap2Page />
        </Suspense>
      }
    />
    <Route
      path="/caremap2/:citySlug"
      element={
        <Suspense fallback={null}>
          <CareMap2Page />
        </Suspense>
      }
    />
    <Route
      path="/healthcontours"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="healthcontours">
            <HealthContoursPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/healthcontours/signup"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="healthcontours">
            <HealthContoursSignupPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/healthcontours/advertise"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="healthcontours">
            <HealthContoursAdvertisePage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    {/* Audience marketing pages. Prefixed here so they never shadow CareMap /
        HealthDesk on app domains; clean `/for-*` roots are added on the
        healthcontours.com host below. */}
    {HEALTHCONTOURS_AUDIENCE_SLUGS.map((slug) => (
      <Route
        key={`healthcontours-audience-${slug}`}
        path={`/healthcontours/${slug}`}
        element={
          <Suspense fallback={<Loading page />}>
            <RequireLauncherApp appId="healthcontours">
              <HealthContoursAudiencePage slug={slug} />
            </RequireLauncherApp>
          </Suspense>
        }
      />
    ))}
    {process.env.NODE_ENV !== "production" && isDevAuthEnvironment() && (
      <Route
        path="/dev/caremap-role-preview"
        element={
          <Suspense fallback={<Loading page />}>
            <CareMapRolePreviewPage />
          </Suspense>
        }
      />
    )}

    <Route
      path="/news"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="news">
            <NewsPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />

    <Route
      path="/fitness"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="fitness">
            <FitnessCalendarPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/fitness/garmin"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="fitness">
            <GarminLinkPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/fitness/garmin/callback"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="fitness">
            <GarminLinkPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/fitness/share"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="fitness">
            <ShareFitnessAccessPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/fitness/patient/:patientUid"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="fitness">
            <ClinicianFitnessViewPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />

    <Route
      path="/email"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="email">
            <EmailInboxPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/email/compose"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="email">
            <EmailComposePage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/email/thread/:threadId"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="email">
            <EmailThreadPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />

    <Route
      path="/asi/trainer"
      element={
        <Suspense fallback={<Loading page />}>
          <AsiTrainerDashboard />
        </Suspense>
      }
    />
    <Route
      path="/asi/manager"
      element={
        <Suspense fallback={<Loading page />}>
          <AsiInteractionManagerDashboard />
        </Suspense>
      }
    />
    <Route
      path="/asi/roles"
      element={
        <Suspense fallback={<Loading page />}>
          <AsiRoleAssignmentPage />
        </Suspense>
      }
    />
    <Route
      path="/asi/global-managers"
      element={
        <RequireGlobalAdmin>
          <Suspense fallback={<Loading page />}>
            <AsiGlobalManagerPage />
          </Suspense>
        </RequireGlobalAdmin>
      }
    />
    <Route
      path="/asi/workflows/:workflowId"
      element={
        <Suspense fallback={<Loading page />}>
          <AsiWorkflowCanvasPage />
        </Suspense>
      }
    />
    <Route
      path="/asi/templates"
      element={
        <Suspense fallback={<Loading page />}>
          <RequireLauncherApp appId="appliedsuperintelligence">
            <AsiTemplateLibraryPage />
          </RequireLauncherApp>
        </Suspense>
      }
    />
    <Route
      path="/asi/run/:interactionId"
      element={
        <Suspense fallback={<Loading page />}>
          <AsiOccurrenceRunPage />
        </Suspense>
      }
    />

    {/* Chat Routes */}
    <Route path="/kijabe-dashboard" element={<KijabeDashboard />} />
    <Route path="/kijabe" element={<KijabeChatPage />} />
    <Route path="/chat" element={<ChatPageWrapper />} />

    {/* Dev-only: WP Ask Me Anything embed preview */}
    {isDevEnvironment() && (
      <Route path="/ask-me-anything" element={<AskMeAnythingPage />} />
    )}

    {/* Virtual Queue Routes */}
    <Route path="/registration/*" element={<PatientRegistration />} />

    {/* Legal Routes */}
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-use" element={<TermsOfUse />} />
    {/* CareVerify facility ToS draft — pending legal review (PROMPT 6) */}
    <Route path="/careverify-terms-draft" element={<CareVerifyTermsDraftPage />} />
    <Route path="/baa" element={<Baa />} />
    {/* <Route path="/chat" element={<PublicChatPage />} /> */}

    {/* Clean audience roots on healthcontours.com (e.g. /for-patients). */}
    {healthContoursHost &&
      HEALTHCONTOURS_AUDIENCE_SLUGS.map((slug) => (
        <Route
          key={`healthcontours-host-audience-${slug}`}
          path={`/${slug}`}
          element={
            <Suspense fallback={<Loading page />}>
              <RequireLauncherApp appId="healthcontours">
                <HealthContoursAudiencePage slug={slug} />
              </RequireLauncherApp>
            </Suspense>
          }
        />
      ))}
    {!fusionMsiHost && (
      <Route
        path="/"
        element={
          clinicalHudHost ? (
            <Suspense fallback={<Loading page />}>
              <ClinicalHudMarketingSplashPage />
            </Suspense>
          ) : md3cHost ? (
            <Suspense fallback={<Loading page />}>
              <Md3cHomePage />
            </Suspense>
          ) : msi3Host ? (
            <Suspense fallback={<Loading page />}>
              <Msi3MarketingPitchPage />
            </Suspense>
          ) : healthContoursHost ? (
            <Suspense fallback={<Loading page />}>
              <RequireLauncherApp appId="healthcontours">
                <HealthContoursPage />
              </RequireLauncherApp>
            </Suspense>
          ) : marketingHost ? (
            <Suspense fallback={<Loading page />}>
              <VoxPontMarketingPage />
            </Suspense>
          ) : asiMarketingHost ? (
            <Suspense fallback={<Loading page />}>
              <AsiMarketingSplashPage />
            </Suspense>
          ) : (
            <RootRedirect />
          )
        }
      />
    )}
    {md3cHost && (
      <>
        <Route
          path="/critical-challenges"
          element={
            <Suspense fallback={<Loading page />}>
              <Md3cCriticalChallengesPage />
            </Suspense>
          }
        />
        <Route
          path="/about-us"
          element={
            <Suspense fallback={<Loading page />}>
              <Md3cAboutPage />
            </Suspense>
          }
        />
        <Route
          path="/care-before-the-visit"
          element={
            <Suspense fallback={<Loading page />}>
              <Md3cBlogIndexPage />
            </Suspense>
          }
        />
        <Route
          path="/care-before-the-visit/f/:slug"
          element={
            <Suspense fallback={<Loading page />}>
              <Md3cEssayPage />
            </Suspense>
          }
        />
      </>
    )}
    {!fusionMsiHost && !msi3Host && (
      <>
        {productRedirects.map((row) => (
          <Route
            key={`product-subdomain-app-${row.path}`}
            path={row.path}
            element={<ProductSubdomainReplace url={row.url} />}
          />
        ))}
        <Route path="/:country/:city" element={<GeoMarketingRootRedirect />} />
        <Route path="/:country" element={<GeoMarketingRootRedirect />} />
      </>
    )}
    <Route
      path="*"
      element={<UnmatchedRouteFallback />}
    />
    </Routes>
    </>
  );
};
